import type { UseOnMenuNavigateHook } from '@rt/global-navi';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';

/**
 * v2 GlobalNavi のメニュークリックを SPA 遷移にする hook (troppus-front 版)。
 *
 * - href が当アプリ basepath (`/troppus`) 配下なら TanStack Router の `navigate` で SPA 遷移し
 *   `true` を返す → auth-lib-front 側で `event.preventDefault()` が呼ばれる。
 * - basepath 外 (別プロダクトへのリンクや cross-domain) は `false` を返し通常 anchor 遷移へフォールバック。
 *
 * troppus は v1 navigation 同様 cmd/ctrl+click による新規タブ対応をしていないため、ここでも素朴な
 * router.navigate を呼ぶ。新規タブ要件が出たら useSimulatedAnchorTag 相当を導入する。
 */
export const useGlobalNaviV2OnMenuNavigate: UseOnMenuNavigateHook = () => {
  const router = useRouter();
  const navigate = useNavigate();

  return useCallback(
    ({ href }: { href: string; event: React.MouseEvent<HTMLAnchorElement> }): boolean => {
      const basepath = router.basepath || '';
      if (basepath && !(href === basepath || href.startsWith(`${basepath}/`))) return false;
      const inner = basepath ? href.slice(basepath.length) || '/' : href;
      const [to, query] = inner.split('?');
      const next = query ? Object.fromEntries(new URLSearchParams(query).entries()) : {};
      navigate({
        to: to as never,
        search: ((prev: Record<string, unknown>) => ({ ...prev, ...next })) as never,
      });
      return true;
    },
    [router.basepath, navigate],
  );
};
